/**
 * Synchronized Highlight & Text Animation Engine powered by Anime.js
 * Animate the background highlight ribbon and heading text together!
 */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Map GSAP/common easings to Anime.js
   */
  function mapEasing(ease) {
    if (!ease) return 'easeOutCubic';
    const clean = ease.trim().toLowerCase();
    const map = {
      'power1.out': 'easeOutQuad',
      'power2.out': 'easeOutCubic',
      'power3.out': 'easeOutQuart',
      'power4.out': 'easeOutQuint',
      'expo.out': 'easeOutExpo',
      'sine.out': 'easeOutSine',
      'back.out': 'easeOutBack',
      'elastic.out': 'easeOutElastic(1, .5)',
      'bounce.out': 'easeOutBounce',
      'linear': 'linear'
    };
    return map[clean] || ease;
  }

  /**
   * Clean text splitting - strictly single &nbsp; between words
   */
  function splitTextNodes(node, splitType) {
    if (node.nodeType === Node.TEXT_NODE) {
      const rawText = node.textContent;
      if (!rawText.trim()) {
        return rawText.includes('\n') ? document.createTextNode('') : document.createTextNode(' ');
      }

      const cleaned = rawText.replace(/\s+/g, ' ');
      const hasLeadingSpace = /^\s/.test(rawText) && !rawText.startsWith('\n');
      const hasTrailingSpace = /\s$/.test(rawText) && !rawText.endsWith('\n');

      const words = cleaned.trim().split(' ');
      const fragment = document.createDocumentFragment();

      if (hasLeadingSpace) {
        const spaceSpan = document.createElement('span');
        spaceSpan.className = 'anime-space';
        spaceSpan.innerHTML = '&nbsp;';
        fragment.appendChild(spaceSpan);
      }

      words.forEach(function (word, index) {
        if (!word) return;

        const wordSpan = document.createElement('span');
        wordSpan.className = 'anime-word';

        if (splitType === 'chars') {
          for (let i = 0; i < word.length; i++) {
            const charSpan = document.createElement('span');
            charSpan.className = 'anime-char';
            charSpan.textContent = word[i];
            wordSpan.appendChild(charSpan);
          }
        } else {
          wordSpan.textContent = word;
        }

        fragment.appendChild(wordSpan);

        if (index < words.length - 1) {
          const spaceSpan = document.createElement('span');
          spaceSpan.className = 'anime-space';
          spaceSpan.innerHTML = '&nbsp;';
          fragment.appendChild(spaceSpan);
        }
      });

      if (hasTrailingSpace) {
        const spaceSpan = document.createElement('span');
        spaceSpan.className = 'anime-space';
        spaceSpan.innerHTML = '&nbsp;';
        fragment.appendChild(spaceSpan);
      }

      return fragment;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (
        node.classList.contains('anime-cursor') ||
        node.classList.contains('anime-word') ||
        node.classList.contains('anime-char') ||
        node.classList.contains('anime-space') ||
        node.classList.contains('highlight-ribbon')
      ) {
        return node;
      }
      const children = Array.from(node.childNodes);
      children.forEach(function (child) {
        const replacement = splitTextNodes(child, splitType);
        if (replacement !== child) {
          node.replaceChild(replacement, child);
        }
      });
      return node;
    }
    return node;
  }

  /**
   * Reset line items to initial state before animation
   */
  function prepareInitialState(lineItem, syncMode) {
    const ribbon = lineItem.querySelector('.highlight-ribbon');
    const textEl = lineItem.querySelector('.highlight-line-text');
    const chars = lineItem.querySelectorAll('.anime-char');
    const words = lineItem.querySelectorAll('.anime-word');

    if (ribbon) {
      ribbon.style.transformOrigin = 'left center';
      if (syncMode === 'wipe_reveal' || syncMode === 'type_expand') {
        ribbon.style.transform = 'scaleX(0)';
        ribbon.style.opacity = '1';
      } else if (syncMode === 'slide_together') {
        ribbon.style.transform = 'translateX(-60px)';
        ribbon.style.opacity = '0';
      } else if (syncMode === 'elastic_pop' || syncMode === 'fade_grow') {
        ribbon.style.transform = 'scale(0.3)';
        ribbon.style.opacity = '0';
      } else {
        ribbon.style.transform = 'scaleX(0)';
      }
    }

    if (syncMode === 'type_expand' || syncMode === 'wipe_reveal') {
      chars.forEach(c => {
        c.style.opacity = '0';
        if (syncMode === 'wipe_reveal') {
          c.style.transform = 'translateX(25px)';
        }
      });
    } else if (syncMode === 'slide_together') {
      textEl.style.transform = 'translateX(-60px)';
      textEl.style.opacity = '0';
    } else if (syncMode === 'elastic_pop' || syncMode === 'fade_grow') {
      textEl.style.transform = 'scale(0.3)';
      textEl.style.opacity = '0';
    }
  }

  /**
   * Play synchronized animation for the heading group
   */
  function playHeadingAnimation(headingGroup) {
    if (headingGroup.dataset.animePlaying === 'true') return;
    const isOnce = headingGroup.getAttribute('data-once') === 'true';
    if (headingGroup.dataset.animeDone === 'true' && isOnce) return;

    if (prefersReducedMotion || typeof window.anime === 'undefined') {
      headingGroup.querySelectorAll('.highlight-ribbon, .highlight-line-text, .anime-char').forEach(el => {
        el.style.transform = 'none';
        el.style.opacity = '1';
      });
      headingGroup.dataset.animeDone = 'true';
      return;
    }

    const syncMode = headingGroup.getAttribute('data-sync-mode') || 'wipe_reveal';
    const lines = Array.from(headingGroup.querySelectorAll('.highlight-line-wrapper'));
    const baseDuration = (parseFloat(headingGroup.getAttribute('data-duration')) || 0.8) * 1000;
    const baseDelay = (parseFloat(headingGroup.getAttribute('data-delay')) || 0.2) * 1000;
    const ease = mapEasing(headingGroup.getAttribute('data-ease') || 'power3.out');

    headingGroup.dataset.animePlaying = 'true';

    // Create main Anime.js Timeline for coordinated execution
    const tl = anime.timeline({
      easing: ease,
      complete: function () {
        headingGroup.dataset.animePlaying = 'false';
        headingGroup.dataset.animeDone = 'true';
      }
    });

    lines.forEach(function (line, index) {
      const ribbon = line.querySelector('.highlight-ribbon');
      const textEl = line.querySelector('.highlight-line-text');
      const chars = line.querySelectorAll('.anime-char');
      const timeOffset = index === 0 ? baseDelay : '-=450';

      if (syncMode === 'wipe_reveal') {
        // Synchronized Wipe: Ribbon expands and characters slide into place together!
        tl.add(
          {
            targets: ribbon,
            scaleX: [0, 1],
            duration: baseDuration,
            easing: 'easeOutCubic'
          },
          timeOffset
        );

        if (chars.length > 0) {
          tl.add(
            {
              targets: chars,
              opacity: [0, 1],
              translateX: [25, 0],
              delay: anime.stagger(22, { start: 100 }),
              duration: baseDuration * 0.75,
              easing: 'easeOutQuart'
            },
            '-=' + (baseDuration * 0.85)
          );
        } else {
          tl.add(
            {
              targets: textEl,
              opacity: [0, 1],
              translateX: [25, 0],
              duration: baseDuration * 0.7
            },
            '-=' + (baseDuration * 0.8)
          );
        }
      } else if (syncMode === 'type_expand') {
        // Typewriter on Ribbon: Ribbon expands while characters type out with cursor!
        tl.add(
          {
            targets: ribbon,
            scaleX: [0, 1],
            duration: baseDuration,
            easing: 'easeOutQuad'
          },
          timeOffset
        );

        if (chars.length > 0) {
          tl.add(
            {
              targets: chars,
              opacity: [0, 1],
              delay: anime.stagger(30, { start: 100 }),
              duration: 30,
              easing: 'linear'
            },
            '-=' + (baseDuration * 0.9)
          );
        }
      } else if (syncMode === 'slide_together') {
        // Slide Together: Both ribbon and text slide in simultaneously
        tl.add(
          {
            targets: [ribbon, textEl],
            translateX: [-50, 0],
            opacity: [0, 1],
            duration: baseDuration,
            easing: ease
          },
          timeOffset
        );
      } else if (syncMode === 'elastic_pop') {
        // Elastic Pop: Ribbon and text pop together with spring bounce
        tl.add(
          {
            targets: [ribbon, textEl],
            scale: [0.3, 1],
            opacity: [0, 1],
            duration: baseDuration * 1.2,
            easing: 'easeOutElastic(1, .6)'
          },
          timeOffset
        );
      } else {
        // Default fade & scale
        tl.add(
          {
            targets: ribbon,
            scaleX: [0, 1],
            duration: baseDuration
          },
          timeOffset
        ).add(
          {
            targets: textEl,
            opacity: [0, 1],
            duration: baseDuration * 0.7
          },
          '-=' + (baseDuration * 0.7)
        );
      }
    });

    headingGroup._animeTimeline = tl;
  }

  /**
   * Play content block animations (paragraphs, buttons in right column)
   */
  function playContentAnimation(el) {
    if (el.dataset.animePlaying === 'true') return;
    const isOnce = el.getAttribute('data-once') === 'true';
    if (el.dataset.animeDone === 'true' && isOnce) return;

    if (prefersReducedMotion || typeof window.anime === 'undefined') {
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.dataset.animeDone = 'true';
      return;
    }

    const animType = (el.getAttribute('data-animate') || 'in-right').toLowerCase();
    const delay = (parseFloat(el.getAttribute('data-delay')) || 0.3) * 1000;
    const duration = (parseFloat(el.getAttribute('data-duration')) || 0.75) * 1000;
    const ease = mapEasing(el.getAttribute('data-ease') || 'power3.out');
    const targets = el._animeTargets || [el];

    anime.remove(targets);
    el.dataset.animePlaying = 'true';

    const config = {
      targets: targets,
      delay: anime.stagger(15, { start: delay }),
      duration: duration,
      easing: ease,
      complete: function () {
        el.dataset.animePlaying = 'false';
        el.dataset.animeDone = 'true';
      }
    };

    if (animType === 'in-left') {
      config.translateX = [-40, 0];
      config.opacity = [0, 1];
    } else if (animType === 'in-up') {
      config.translateY = [35, 0];
      config.opacity = [0, 1];
    } else if (animType === 'blur-in') {
      config.filter = ['blur(10px)', 'blur(0px)'];
      config.opacity = [0, 1];
    } else {
      // in-right default
      config.translateX = [40, 0];
      config.opacity = [0, 1];
    }

    anime(config);
  }

  /**
   * Observe section and trigger animations
   */
  function initHighlightSection(section) {
    const headingGroup = section.querySelector('.highlight-lines-group');
    const contentBlocks = section.querySelectorAll('[data-content-animate]');

    // Split heading lines into chars
    if (headingGroup && !headingGroup.dataset.animeInitialized) {
      headingGroup.querySelectorAll('.highlight-line-text').forEach(function (textEl) {
        splitTextNodes(textEl, 'chars');
      });

      const syncMode = headingGroup.getAttribute('data-sync-mode') || 'wipe_reveal';
      headingGroup.querySelectorAll('.highlight-line-wrapper').forEach(function (line) {
        prepareInitialState(line, syncMode);
      });

      headingGroup.dataset.animeInitialized = 'true';
    }

    // Prepare content blocks
    contentBlocks.forEach(function (block) {
      if (!block.dataset.animeInitialized) {
        const splitType = block.getAttribute('data-split') || 'words';
        if (splitType === 'words' || splitType === 'chars') {
          splitTextNodes(block, splitType);
        }
        block._animeTargets =
          splitType === 'chars'
            ? Array.from(block.querySelectorAll('.anime-char'))
            : splitType === 'words'
            ? Array.from(block.querySelectorAll('.anime-word'))
            : [block];

        if (block._animeTargets.length === 0) block._animeTargets = [block];

        block._animeTargets.forEach(t => {
          t.style.opacity = '0';
          t.style.transform = 'translateX(40px)';
        });
        block.dataset.animeInitialized = 'true';
      }
    });

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          const isOnce = section.getAttribute('data-once') === 'true';

          if (entry.isIntersecting) {
            if (headingGroup) playHeadingAnimation(headingGroup);
            contentBlocks.forEach(playContentAnimation);

            if (isOnce) {
              observer.unobserve(section);
            }
          } else {
            // Reset when scrolling away for smooth replay when scrolling back
            if (!isOnce) {
              if (headingGroup) {
                if (headingGroup._animeTimeline) {
                  headingGroup._animeTimeline.pause();
                }
                const syncMode = headingGroup.getAttribute('data-sync-mode') || 'wipe_reveal';
                headingGroup.querySelectorAll('.highlight-line-wrapper').forEach(function (line) {
                  prepareInitialState(line, syncMode);
                });
                headingGroup.dataset.animeDone = 'false';
                headingGroup.dataset.animePlaying = 'false';
              }

              contentBlocks.forEach(function (block) {
                if (block._animeTargets) {
                  anime.remove(block._animeTargets);
                  block._animeTargets.forEach(t => {
                    t.style.opacity = '0';
                    t.style.transform = 'translateX(40px)';
                  });
                }
                block.dataset.animeDone = 'false';
                block.dataset.animePlaying = 'false';
              });
            }
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    observer.observe(section);
  }

  function run() {
    const sections = document.querySelectorAll('[data-highlight-section]');
    if (sections.length === 0) return;

    if (typeof window.anime !== 'undefined') {
      sections.forEach(initHighlightSection);
    } else {
      let attempts = 0;
      const interval = setInterval(function () {
        attempts++;
        if (typeof window.anime !== 'undefined') {
          clearInterval(interval);
          sections.forEach(initHighlightSection);
        } else if (attempts > 50) {
          clearInterval(interval);
          document.querySelectorAll('.highlight-ribbon, .highlight-line-text, [data-content-animate]').forEach(el => {
            el.style.opacity = '1';
            el.style.transform = 'none';
          });
        }
      }, 50);
    }
  }

  // Safety fallback after 3s
  setTimeout(function () {
    document.querySelectorAll('.highlight-ribbon, .highlight-line-text').forEach(el => {
      if (el.style.opacity === '0' || el.style.transform === 'scaleX(0)') {
        el.style.opacity = '1';
        el.style.transform = 'none';
      }
    });
  }, 3000);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Shopify Editor events
  document.addEventListener('shopify:section:load', run);
  document.addEventListener('shopify:section:select', run);
})();
